import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { featureLabel, formatDate, formatKrw, formatNum, sourceColor, sourceLabel } from '../lib/format';

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => adminApi.getUser(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="text-sm text-text-muted">로딩 중…</div>;
  }
  if (isError || !data) {
    return (
      <div className="card text-sm text-error">
        불러오기 실패: {(error as { error?: string })?.error ?? '알 수 없음'}
      </div>
    );
  }

  const totalMonthCost = data.this_month_usage.reduce((a, b) => a + b.cost, 0);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/users" className="text-xs text-text-muted hover:text-text-secondary">
          ← 회원 목록
        </Link>
        <h1 className="text-xl font-semibold text-text-primary mt-2">
          {data.user.name ?? '(이름 없음)'}
        </h1>
        <div className="text-sm text-text-secondary mt-1">
          {data.user.company_name ?? '회사 미입력'} · {data.user.company_role ?? '대표'}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="전화" value={data.user.phone_e164} mono />
        <Stat label="이메일" value={data.user.email ?? '—'} />
        <Stat label="가입" value={formatDate(data.user.created_at)} />
        <Stat label="온보딩" value={data.user.onboarded_at ? formatDate(data.user.onboarded_at) : '미완료'} />
      </div>

      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-3">활동</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="통화" value={formatNum(data.activity.calls)} />
          <Stat label="채팅 메시지" value={formatNum(data.activity.chat_messages)} />
          <Stat label="TODO" value={formatNum(data.activity.todos)} />
          <Stat label="요약" value={formatNum(data.activity.summaries)} />
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary">이번달 사용량 / 비용</h2>
          <div className="text-sm text-text-primary font-medium">
            합계 {formatKrw(totalMonthCost, true)}
          </div>
        </div>
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-elev text-text-secondary">
              <tr>
                <Th>소스</Th>
                <Th>기능</Th>
                <Th className="text-right">호출 수</Th>
                <Th className="text-right">입력 단위</Th>
                <Th className="text-right">출력 단위</Th>
                <Th className="text-right">비용</Th>
              </tr>
            </thead>
            <tbody>
              {data.this_month_usage.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    이번달 호출 없음
                  </td>
                </tr>
              )}
              {data.this_month_usage.map((row) => (
                <tr key={`${row.source}-${row.feature}`} className="border-t border-border">
                  <Td>
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: sourceColor(row.source) }}
                      />
                      {sourceLabel(row.source)}
                    </span>
                  </Td>
                  <Td className="text-text-secondary">{featureLabel(row.feature)}</Td>
                  <Td className="text-right text-text-secondary">{formatNum(row.events)}</Td>
                  <Td className="text-right text-text-muted text-xs">
                    {formatNum(Math.round(row.input_units))}
                  </Td>
                  <Td className="text-right text-text-muted text-xs">
                    {row.output_units > 0 ? formatNum(Math.round(row.output_units)) : '—'}
                  </Td>
                  <Td className="text-right text-text-primary font-medium">
                    {formatKrw(row.cost, true)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="card">
      <div className="text-[11px] uppercase tracking-wide text-text-muted">{label}</div>
      <div className={`mt-1 text-sm text-text-primary ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${className ?? ''}`}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-middle ${className ?? ''}`}>{children}</td>;
}
